import { Chip, Typography } from '@execonline-inc/execonline-ui';
import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';
import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { ComponentType, SVGProps } from 'react';
import { T, TPlainTextKey } from '../../../../../../Translations';
import { PluralizedText } from '../../../../../PluralizedText';
import { showsPreviewCount, UnlicensedAccess } from '../HiddenCourseTreatment';
import {
  CollectionLicenseOption,
  Coverage,
  LicenseGroup as Group,
  ProgramFamily,
} from '../LicenseAlertsStore/Types';
import { isAssignable } from '../LicenseAvailability';
import { coversNothing } from '../Selectors';
import CoursesNeedingALicense from './CoursesNeedingALicense';
import ParticipantRows from './ParticipantRows';

interface Props {
  group: Group;
  coursesNeedingALicense: ReadonlyArray<ProgramFamily>;
  enrollableCourseCount: number;
  totalCourseCount: number;
  licenseOptions: ReadonlyArray<CollectionLicenseOption>;
  unknownEmails: ReadonlyArray<string>;
  access: UnlicensedAccess;
  // At one person the row's own chip just repeats the active tab.
  showsCoverageChip: boolean;
  open: boolean;
  onToggle: () => void;
}

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

const coverageIcon = (coverage: Coverage): HeroIcon => {
  switch (coverage) {
    case 'full':
      return CheckCircleIcon;
    case 'partial':
      return LockOpenIcon;
    case 'none':
      return LockClosedIcon;
    default:
      return assertNever(coverage);
  }
};

const coverageIconClassName = (coverage: Coverage): string => {
  switch (coverage) {
    case 'full':
      return 'text-accent';
    case 'partial':
      return 'text-primary';
    case 'none':
      return 'text-destructive';
    default:
      return assertNever(coverage);
  }
};

// Chip's own colour props are literal palette names, so semantic classes
// override them; Chip composes with twMerge.
const coverageChipClassName = (coverage: Coverage): string => {
  switch (coverage) {
    case 'full':
      return 'bg-accent/10 text-accent';
    case 'partial':
      return 'bg-primary/10 text-primary';
    case 'none':
      return 'bg-destructive/10 text-destructive';
    default:
      return assertNever(coverage);
  }
};

const coverageLabel = (coverage: Coverage): TPlainTextKey => {
  switch (coverage) {
    case 'full':
      return 'Fully licensed';
    case 'partial':
      return 'Partially licensed';
    case 'none':
      return 'Not licensed';
    default:
      return assertNever(coverage);
  }
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

function LicenseGroup({
  group,
  coursesNeedingALicense,
  enrollableCourseCount,
  totalCourseCount,
  licenseOptions,
  unknownEmails,
  access,
  showsCoverageChip,
  open,
  onToggle,
}: Props) {
  const participantCount = group.participants.length + unknownEmails.length;
  const shortfall = group.hiddenProgramFamilyIds.length;
  const ChevronIcon = open ? ChevronUpIcon : ChevronDownIcon;
  const CoverageIcon = coverageIcon(group.coverage);

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-md border',
        coversNothing(group.coverage) ? 'border-destructive' : 'border-border',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 p-3 text-left hover:bg-secondary"
        data-intercom-target={`share-license-group-${group.key}`}
      >
        <CoverageIcon
          className={clsx('mt-0.5 h-5 w-5 flex-none', coverageIconClassName(group.coverage))}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="subtitleSmall" as="span" fontWeight="semibold">
              {group.primaryLicenseLabel
                .map((label) => <span key="label">{label}</span>)
                .getOrElse(() => (
                  <T kind="Not licensed" />
                ))}
            </Typography>
            {showsCoverageChip && (
              <Chip className={coverageChipClassName(group.coverage)} size="sm" radius="full">
                <T kind={coverageLabel(group.coverage)} />
              </Chip>
            )}
            {group.expiresOn
              .map((expiresOn) => (
                <Chip
                  key="expiry"
                  className="bg-destructive/10 text-destructive"
                  size="sm"
                  radius="full"
                >
                  <CalendarIcon className="mr-1 inline h-3 w-3" aria-hidden="true" />
                  <T
                    kind="Expires {{date}}, before this collection ends"
                    date={formatDate(expiresOn)}
                  />
                </Chip>
              ))
              .getOrElseValue(<></>)}
          </div>
          <Typography variant="caption" className="mt-1 block text-muted-foreground">
            {participantCount}{' '}
            <PluralizedText count={participantCount} singularKind="person" pluralKind="people" />{' '}
            &middot;{' '}
            <T
              kind="can enroll in {{enrollable}} of {{total}} courses"
              enrollable={enrollableCourseCount}
              total={totalCourseCount}
            />
            {shortfall > 0 && showsPreviewCount(access) && (
              <>
                {' '}
                &middot; {shortfall} <T kind="preview only" />
              </>
            )}
          </Typography>
        </div>
        <ChevronIcon
          className="mt-0.5 h-5 w-5 flex-none text-muted-foreground"
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="border-t border-border">
          {group.primaryLicenseLabel.isNothing() && (
            <div className="flex flex-col gap-3 border-b border-border p-4">
              <Typography variant="bodyMedium">
                <T kind="They won't receive an invitation until someone assigns them a license." />
              </Typography>
              {unknownEmails.length > 0 && (
                <Typography variant="bodyMedium" className="text-muted-foreground">
                  <T kind="Addresses that aren't in your organization won't receive an invitation at all." />
                </Typography>
              )}
              {!licenseOptions.some((option) => isAssignable(option.status)) && (
                <Typography variant="bodyMedium" className="text-muted-foreground">
                  <T kind="Your organization holds no license that covers this collection." />
                </Typography>
              )}
            </div>
          )}
          {coursesNeedingALicense.length > 0 && (
            <CoursesNeedingALicense
              courses={coursesNeedingALicense}
              initiallyOpen={coversNothing(group.coverage)}
            />
          )}
          <ParticipantRows
            participants={group.participants}
            unknownEmails={unknownEmails}
            removable={coversNothing(group.coverage)}
          />
        </div>
      )}
    </div>
  );
}

export default LicenseGroup;
